<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import FeaturedIcon from '@/common/components/molecules/featuredIcon/FeaturedIcon.vue';
import SimpleTextTooltip from '@/common/components/molecules/simpleTextTooltip/SimpleTextTooltip.vue';
import { type Props, setup } from './dashboardElement.setup';

const props = defineProps<Props>();

const {
  isFavorited,
  isAnalysisFavorited,
  favoriteRef,
  tooltipMouseEvent,
  onMouseEventOnText,
  onClickElement,
} = setup(props);
</script>

<template>
  <li
    :class="['dashboard-menu-element sub-menu-element', { selected }]"
    @click="onClickElement"
  >
    <div class="dashboard-menu-element__name">
      <featured-icon
        v-if="dashboardInfo.featuredIcon"
        icon-color="white"
        :icon-code="dashboardInfo.featuredIcon"
      />
      <ev-icon
        v-else
        class="dashboard-menu-element__default-icon"
        icon="icon-dashboard"
        size="small"
        style="background-color: #ffffff"
      />
      <span
        @mouseenter="onMouseEventOnText"
        @mouseleave="onMouseEventOnText"
      >
        {{ dashboardInfo.text }}
      </span>
    </div>
    <base-button
      ref="favoriteRef"
      tag="button"
      :class="[
        'dashboard-menu-element__fav',
        { 'dashboard-menu-element__fav--active': isFavorited || isAnalysisFavorited },
      ]"
    >
      <ev-icon :icon="isFavorited || isAnalysisFavorited ? 'icon-fill-star' : 'icon-star'" />
    </base-button>
    <simple-text-tooltip
      :mouse-event="tooltipMouseEvent"
      base-pos="top"
      custom-class="dashboard-menu-element__tooltip"
      :text="dashboardInfo.text"
    />
  </li>
</template>

<style lang="scss">
.dashboard-menu-element {
  gap: 8px;
  justify-content: flex-start;

  &__fav {
    display: flex;
    align-items: center;
    width: 20px;
    height: 20px;
    cursor: pointer;
    border-radius: 2px;
    background-color: transparent;

    &:hover {
      background-color: rgb(var(--color-gray-00-00-rgb), 0.06);
    }

    i {
      width: 20px;
      height: 20px;
      mask-size: 18px;
      background-color: var(--color-gray-03-09);
    }

    &--active i {
      background-color: var(--color-yellow-05-05);
    }

    &:not(.dashboard-menu-element__fav--active) {
      opacity: 0;
    }
  }

  &:hover &__fav:not(&__fav--active),
  &__fav:not(&__fav--active):focus-visible {
    opacity: 1;
  }

  &__name {
    display: inline-flex;
    flex: 1;
    gap: 12px;
    align-items: center;
    min-width: 0;
    cursor: pointer;
    background-color: transparent;
    color: inherit;
    text-align: start;

    span {
      display: inline-block;
      font-size: 13px;
      font-weight: 400;
      line-height: 1.2;
      letter-spacing: 0.2px;
      color: var(--color-gray-00-00);

      @include shortening();
    }
  }

  &:hover:not(.selected) {
    background-color: var(--gnb-icon-hover-bg-color);
  }

  &__default-icon {
    background-color: #ffffff;
  }
}
.text-tooltip.dashboard-menu-element__tooltip {
  background-color: var(--color-gray-05-12);
  color: var(--color-gray-00-00);

  &.show-tail.top::before {
    border-top-color: var(--color-gray-05-12);
  }
}

@include max-screen() {
  .dashboard-menu-element__fav {
    i {
      mask-size: 16px;
    }
  }
}
</style>
